import { DEFAULT_CONTACT_COST } from "app/pages/sales-intelligence/modules/quota/constants";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";

import type {
    CompanyInformationQuota,
    DataCreditsContactsCost,
    DataCreditsStateDto,
    QuotaOperationState,
} from "../../types";
import { quotaQueryKeys } from "../keys";

import type { IQuotaQueryClient, IQuotaQueryClientDeps } from "./types";

export const createQuotaQueryClient = ({
    salesSettingsHelper,
    queryClient,
}: IQuotaQueryClientDeps): IQuotaQueryClient => {
    const getCompanyInformationQuota = (): Promise<CompanyInformationQuota> => {
        return queryClient.fetchQuery<CompanyInformationQuota>({
            queryKey: quotaQueryKeys.companyInformationQuota(),
            queryFn: ({ signal }) => salesApiService.quota.fetchCompaniesQuota({ cancellation: signal }),
        });
    };

    const getCompanyInformationQuotaFromCache = (): CompanyInformationQuota | undefined => {
        return queryClient.getQueryData<CompanyInformationQuota>(quotaQueryKeys.companyInformationQuota());
    };

    const getIsCompanyQueryLimiterEnabled = async () => {
        const prevQuota = getCompanyInformationQuotaFromCache();

        if (!!prevQuota && prevQuota.isUnlimited) {
            return false;
        }

        if (prevQuota && prevQuota.remaining <= 0) {
            return true;
        }

        try {
            const quota = await getCompanyInformationQuota();

            return !!quota && !quota.isUnlimited && quota.remaining <= 0;
        } catch {
            return false;
        }
    };

    const invalidateCompanyInformationQuota = (): Promise<void> => {
        return queryClient.invalidateQueries({ queryKey: quotaQueryKeys.companyInformationQuota() });
    };

    const prefetchDataCreditsState = (): Promise<void> => {
        return queryClient.prefetchQuery({
            queryKey: quotaQueryKeys.dataCredits(),
            queryFn: () => salesApiService.quota.fetchDataCreditsState(),
        });
    };

    const getDataCreditsState = (): Promise<DataCreditsStateDto> => {
        return queryClient.fetchQuery({
            queryKey: quotaQueryKeys.dataCredits(),
            queryFn: ({ signal }) => salesApiService.quota.fetchDataCreditsState({ cancellation: signal }),
        });
    };

    const getContactsDataCreditsCosts = (): Promise<DataCreditsContactsCost> => {
        return queryClient.fetchQuery({
            queryKey: quotaQueryKeys.dataCreditsContactsCost(),
            queryFn: ({ signal }) =>
                salesApiService.quota.fetchStaticContactsDataCreditsCosts({ cancellation: signal }),
        });
    };

    const invalidateDataCreditsState = (): Promise<void> => {
        return queryClient.invalidateQueries({ queryKey: quotaQueryKeys.dataCredits() });
    };

    const updateDataCreditsExportState = (
        newExportsState: Partial<QuotaOperationState> | ((oldData: QuotaOperationState) => QuotaOperationState),
    ): void => {
        queryClient.setQueryData(quotaQueryKeys.dataCredits(), (oldData: DataCreditsStateDto | undefined) => {
            if (!oldData) {
                return oldData;
            }

            return {
                ...oldData,
                exportsState: {
                    ...oldData.exportsState,
                    ...(typeof newExportsState === "function"
                        ? newExportsState(oldData.exportsState)
                        : newExportsState),
                },
            };
        });
    };

    const getExportQuota = async (): Promise<QuotaOperationState> => {
        if (salesSettingsHelper.isDataCreditsEnabled) {
            const dataCredits = await getDataCreditsState();

            return dataCredits.exportsState;
        }

        return queryClient.fetchQuery<QuotaOperationState>({
            queryKey: quotaQueryKeys.excelQuota(),
            queryFn: ({ signal }) => salesApiService.quota.fetchExcelQuota({ cancellation: signal }),
        });
    };

    const invalidateExportQuota = (): Promise<void> => {
        if (salesSettingsHelper.isDataCreditsEnabled) {
            return invalidateDataCreditsState();
        }

        return queryClient.invalidateQueries({ queryKey: quotaQueryKeys.excelQuota() });
    };

    const getContactsQuota = async (): Promise<QuotaOperationState> => {
        if (salesSettingsHelper.isDataCreditsEnabled) {
            const dataCredits = await getDataCreditsState();

            return dataCredits.exportsState;
        }

        return queryClient.fetchQuery<QuotaOperationState>({
            queryKey: quotaQueryKeys.contactsQuota(),
            queryFn: ({ signal }) => salesApiService.quota.fetchContactsQuota({ cancellation: signal }),
        });
    };

    const getContactCost = async (): Promise<number> => {
        if (!salesSettingsHelper.isDataCreditsEnabled) {
            return DEFAULT_CONTACT_COST;
        }

        const cost = await getContactsDataCreditsCosts();

        return cost.costPerRecord;
    };

    const invalidateContactsQuota = (): Promise<void> => {
        if (salesSettingsHelper.isDataCreditsEnabled) {
            return invalidateDataCreditsState();
        }

        return queryClient.invalidateQueries({ queryKey: quotaQueryKeys.contactsQuota() });
    };

    return {
        getCompanyInformationQuota,
        getCompanyInformationQuotaFromCache,
        getIsCompanyQueryLimiterEnabled,
        invalidateCompanyInformationQuota,

        prefetchDataCreditsState,
        getDataCreditsState,
        invalidateDataCreditsState: invalidateDataCreditsState,
        updateDataCreditsExportState,

        getExportQuota,
        invalidateExportQuota,

        getContactsQuota,
        getContactCost,
        invalidateContactsQuota,
    };
};
